// src/components/CommunicationLogManagement.js

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import 'animate.css';
import { usePermissions } from '../App'; // Import the permissions hook

function CommunicationLogManagement({
  clients, // Needed for client dropdown
  cases, // Needed for case dropdown
  communicationLogs,
  users, // Needed for logged_by dropdown (though logged_by is auto-set by backend usually)
  authAxios,
  fetchCommunicationLogs,
  showFormMessage,
  handleDelete, // Generic delete function from App.js
  currentUser, // Needed for setting logged_by
  // Options for dropdowns (passed from App.js)
  communicationLogTypeOptions,
  setCommunicationLogs, // Added setCommunicationLogs for immediate UI update
  navigateTo,
  setNavigateTo,
  setActiveTab
}) {
  const permissions = usePermissions(); // Use the permissions hook
  // Communication Log Management specific states
  const [showAddCommunicationLogForm, setShowAddCommunicationLogForm] = useState(false);
  const [communicationLogsViewMode, setCommunicationLogsViewMode] = useState('tiles');
  
  // --- NEW: State for filtering and searching ---
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterCase, setFilterCase] = useState('');
  const [filterInvoice, setFilterInvoice] = useState('');
  const [filterDebtRecoveryReference, setFilterDebtRecoveryReference] = useState('');
  
  // New communication log form data
  const [newCommunicationLog, setNewCommunicationLog] = useState({
    client: '', 
    case: '', 
    debt_recovery_request: '',
    invoice: '',
    log_type: 'Note', // FIX: Default to a valid choice from the backend model
    subject: '', // FIX: Changed from 'summary' to 'subject'
    body: '', // FIX: Changed from 'details' to 'body'
    attachment: null,
  });

  const [statusChangeContext, setStatusChangeContext] = useState(null);
  const [statusGuidance, setStatusGuidance] = useState(null);
  const [statusReason, setStatusReason] = useState('');
  const [statusFollowUpDate, setStatusFollowUpDate] = useState('');
  const [statusCommitments, setStatusCommitments] = useState('');
  const [statusCreateSettlementTask, setStatusCreateSettlementTask] = useState(false);

  // States for editing communication logs
  const [showEditCommunicationLogModal, setShowEditCommunicationLogModal] = useState(false);
  const [editingCommunicationLog, setEditingCommunicationLog] = useState(null);
  const [editingAttachment, setEditingAttachment] = useState(null);

  // States for viewing communication log details
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLogForDetails, setSelectedLogForDetails] = useState(null);

  // --- Helper function to get client's full name for dropdowns ---
  const getClientDisplayName = useCallback((client) => {
    if (!client) return '';
    if (client.client_type === 'individual' || (client.first_name && client.last_name)) {
      return `${client.first_name || ''} ${client.last_name || ''}`.trim();
    } else if (client.client_type === 'organizational' || client.organization_name) {
      return client.organization_name || 'Organizational Client';
    }
    return `Client ID: ${client.id}`; // Fallback
  }, []);
  
  // --- ENHANCEMENT: Memoized filtering for communication logs ---
  const filteredCommunicationLogs = useMemo(() => {
    return communicationLogs.filter(log => {
      const searchTermLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm ? 
        (log.subject?.toLowerCase().includes(searchTermLower) || 
         log.body?.toLowerCase().includes(searchTermLower) ||
         log.client_full_name?.toLowerCase().includes(searchTermLower) ||
         log.case_title?.toLowerCase().includes(searchTermLower))
        : true;
        
      const matchesClient = filterClient ? log.client === parseInt(filterClient) : true;
      const matchesCase = filterCase ? log.case === parseInt(filterCase) : true;
      const matchesInvoice = filterInvoice ? log.invoice === parseInt(filterInvoice) : true;
      const matchesDebtRecovery = filterDebtRecoveryReference ? String(log.debt_recovery_request_reference || '').toLowerCase().includes(String(filterDebtRecoveryReference).toLowerCase()) : true;
      
      return matchesSearch && matchesClient && matchesCase && matchesInvoice && matchesDebtRecovery;
    });
  }, [communicationLogs, searchTerm, filterClient, filterCase, filterInvoice, filterDebtRecoveryReference]);
  
  // --- ENHANCEMENT: Dynamically filter cases based on selected client in forms ---
  const availableCasesForNewLog = useMemo(() => {
    if (!newCommunicationLog.client) return cases;
    return cases.filter(_case => _case.client === parseInt(newCommunicationLog.client));
  }, [cases, newCommunicationLog.client]);

  const availableCasesForEditLog = useMemo(() => {
    if (!editingCommunicationLog?.client) return cases;
    return cases.filter(_case => _case.client === parseInt(editingCommunicationLog.client));
  }, [cases, editingCommunicationLog]);

  // --- Helper to get badge color for log type ---
  const getBadgeColor = (logType) => {
    switch (logType) {
        case 'Email': return 'blue';
        case 'Call': return 'green';
        case 'Meeting': return 'yellow';
        case 'Note': return 'gray';
        default: return 'indigo';
    }
  };

  const openDocumentsForDrr = useCallback((mode) => {
    if (typeof setActiveTab === 'function') {
      setActiveTab('documents');
    }
    if (typeof setNavigateTo === 'function') {
      setNavigateTo({
        tab: 'documents',
        type: 'drr_document',
        mode,
        debtRecoveryRequestId: statusGuidance?.requestId,
        clientId: statusGuidance?.clientId,
        caseId: statusGuidance?.caseId,
      });
    }
  }, [setActiveTab, setNavigateTo, statusGuidance]);

  const handleCreateCaseFromDrr = useCallback(async () => {
    if (!statusGuidance?.requestId) return;
    try {
      const resp = await authAxios.post(`debt-recovery-requests/${statusGuidance.requestId}/create-case/`, {});
      showFormMessage?.('success', 'Case created from debt recovery request.');
      const caseId = resp?.data?.id;
      if (caseId && typeof setActiveTab === 'function') {
        setActiveTab('cases');
        setNavigateTo?.({ tab: 'cases', id: caseId, type: 'case_open', autoOpen: true });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to create case from debt recovery request.';
      showFormMessage?.('danger', msg);
    }
  }, [authAxios, statusGuidance, setActiveTab, setNavigateTo, showFormMessage]);

  const handleSmsPlaceholder = useCallback(() => {
    showFormMessage?.('warning', 'SMS sending is not yet enabled. Log the SMS details in this communication log for now.');
  }, [showFormMessage]);


  // --- Communication Log Form Input Change Handlers ---
  const handleCommunicationLogInputChange = (e) => {
    const { name, value } = e.target;
    const isFile = e.target.type === 'file';
    const file = isFile ? (e.target.files && e.target.files.length ? e.target.files[0] : null) : null;
    setNewCommunicationLog(prev => {
      const updatedLog = { ...prev, [name]: isFile ? file : value };
      // If the client changes, reset the case
      if (name === 'client') {
        updatedLog.case = '';
      }
      return updatedLog;
    });
  };
  
  const handleEditingCommunicationLogChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditingCommunicationLog(prev => {
        const updatedLog = { ...prev, [name]: value };
        // If the client changes, reset the case
        if (name === 'client') {
            updatedLog.case = '';
        }
        return updatedLog;
    });
  }, []);

  const handleEditingAttachmentChange = useCallback((e) => {
    const file = e.target.files && e.target.files.length ? e.target.files[0] : null;
    setEditingAttachment(file);
  }, []);

  // --- Communication Log Form Submission Handler ---
  const handleCommunicationLogSubmit = useCallback(async (e) => {
    e.preventDefault();
    showFormMessage('', ''); // Clear previous messages

    try {
      const requiresReason = statusChangeContext && ['WRITTEN_OFF', 'CLOSED'].includes(statusChangeContext.nextStatus);
      if (requiresReason && !statusReason.trim()) {
        showFormMessage('danger', 'A reason is required to close or write off a debt recovery request.');
        return;
      }

      let bodyText = String(newCommunicationLog.body || '').trim();
      const extraLines = [];
      if (statusReason.trim()) extraLines.push(`Reason: ${statusReason.trim()}`);
      if (statusFollowUpDate) extraLines.push(`Next follow-up date: ${statusFollowUpDate}`);
      if (statusCommitments.trim()) extraLines.push(`Commitments: ${statusCommitments.trim()}`);
      if (extraLines.length) {
        bodyText = [bodyText, '', ...extraLines].filter(Boolean).join('\n');
      }

      const basePayload = {
        ...newCommunicationLog,
        client: newCommunicationLog.client || null,
        case: newCommunicationLog.case || null,
        debt_recovery_request: newCommunicationLog.debt_recovery_request || null,
        invoice: newCommunicationLog.invoice || null,
        body: bodyText,
        action: statusChangeContext ? 'debt_recovery_status_capture' : newCommunicationLog.action,
      };

      let response;
      if (newCommunicationLog.attachment) {
        const formData = new FormData();
        Object.entries(basePayload).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return;
          formData.append(key, value);
        });
        formData.append('attachment', newCommunicationLog.attachment);
        response = await authAxios.post('communication-logs/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        response = await authAxios.post('communication-logs/', basePayload);
      }

      if (statusChangeContext?.requestId && statusChangeContext?.nextStatus) {
        await authAxios.patch(`debt-recovery-requests/${statusChangeContext.requestId}/`, { status: statusChangeContext.nextStatus });

        if (statusCreateSettlementTask && statusChangeContext.nextStatus === 'NEGOTIATION') {
          const taskPayload = {
            title: 'Draft Settlement Document',
            description: `Draft settlement document for DRR ${statusChangeContext.requestReference || statusChangeContext.requestId}.`,
            debt_recovery_request: statusChangeContext.requestId,
            status: 'To-Do',
            priority: 'Medium',
          };
          await authAxios.post('tasks/', taskPayload);
        }
      }
      showFormMessage('success', 'Communication Log added successfully!');
      setShowAddCommunicationLogForm(false); // Close form on success
      setNewCommunicationLog({ // Reset form
        client: '', case: '', debt_recovery_request: '', invoice: '', log_type: 'Note', subject: '', body: '', attachment: null,
      });
      setStatusChangeContext(null);
      setStatusGuidance(null);
      setStatusReason('');
      setStatusFollowUpDate('');
      setStatusCommitments('');
      setStatusCreateSettlementTask(false);

      fetchCommunicationLogs();

    } catch (error) {
      console.error("Frontend: Error adding Communication Log:", error.response ? error.response.data : error.message);
      let errorMessage = 'Failed to add communication log.';
      if (error.response && error.response.data) {
        const errors = error.response.data;
        const errorDetails = Object.keys(errors)
          .map(key => `${key}: ${errors[key].join(', ')}`)
          .join(' ');
        errorMessage = `${errorMessage} ${errorDetails}`;
      }
      showFormMessage('danger', errorMessage);
    }
  }, [newCommunicationLog, authAxios, fetchCommunicationLogs, showFormMessage, statusChangeContext, statusReason, statusFollowUpDate, statusCommitments, statusCreateSettlementTask]);


  // --- Edit Communication Log Modal Handlers ---
  const openEditCommunicationLogModal = useCallback((log) => {
    setEditingCommunicationLog({ ...log });
    setEditingAttachment(null);
    setShowEditCommunicationLogModal(true);
  }, []);

  const closeEditCommunicationLogModal = useCallback(() => {
    setEditingCommunicationLog(null);
    setEditingAttachment(null);
    setShowEditCommunicationLogModal(false);
  }, []);

  const handleEditCommunicationLogSubmit = useCallback(async (e) => {
    e.preventDefault();
    showFormMessage('', ''); // Clear previous messages

    if (!editingCommunicationLog) return;

    try {
      const payload = {
        ...editingCommunicationLog,
        client: editingCommunicationLog.client || null,
        case: editingCommunicationLog.case || null,
      };
      if (editingAttachment) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return;
          formData.append(key, value);
        });
        formData.append('attachment', editingAttachment);
        await authAxios.patch(`communication-logs/${editingCommunicationLog.id}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await authAxios.patch(`communication-logs/${editingCommunicationLog.id}/`, payload);
      }
      showFormMessage('success', 'Communication Log updated successfully!');
      closeEditCommunicationLogModal();
      fetchCommunicationLogs(); // Re-fetch all communication logs to ensure data consistency
    } catch (error) {
      console.error("Frontend: Error updating Communication Log:", error.response ? error.response.data : error.message);
      const errorMessage = error.response?.data?.detail || 'Failed to update communication log.';
      showFormMessage('danger', errorMessage);
    }
  }, [editingCommunicationLog, editingAttachment, authAxios, fetchCommunicationLogs, showFormMessage, closeEditCommunicationLogModal]);

  const resetAddLogForm = useCallback(() => {
    setShowAddCommunicationLogForm(false);
    setNewCommunicationLog({ client: '', case: '', debt_recovery_request: '', invoice: '', log_type: 'Note', subject: '', body: '', attachment: null });
    setStatusChangeContext(null);
    setStatusGuidance(null);
    setStatusReason('');
    setStatusFollowUpDate('');
    setStatusCommitments('');
    setStatusCreateSettlementTask(false);
  }, []);


  // --- View Details Modal Handlers ---
  const openDetailsModal = useCallback((log) => {
    setSelectedLogForDetails(log);
    setShowDetailsModal(true);
  }, []);

  const closeDetailsModal = useCallback(() => {
    setSelectedLogForDetails(null);
    setShowDetailsModal(false);
  }, []);

  useEffect(() => {
    if (navigateTo && navigateTo.tab === 'communicationLogs' && navigateTo.invoiceId) {
      // Pre-filter to a specific invoice (used by Debt Collection Dashboard shortcut)
      const invoiceIdStr = String(navigateTo.invoiceId);
      setFilterInvoice(invoiceIdStr);
      setFilterDebtRecoveryReference('');
      setSearchTerm('');
      setFilterClient('');
      setFilterCase('');
      setCommunicationLogsViewMode('list');

      // If the navigation is only for filtering, clear it immediately.
      if (!navigateTo.id) {
        setNavigateTo(null);
      }
    }
  }, [navigateTo, setNavigateTo]);

  useEffect(() => {
    if (navigateTo && navigateTo.tab === 'communicationLogs' && (navigateTo.debtRecoveryRequestReference || navigateTo.debtRecoveryRequestId)) {
      const ref = navigateTo.debtRecoveryRequestReference || navigateTo.debtRecoveryRequestId;
      const refStr = String(ref || '');
      setFilterDebtRecoveryReference(refStr);
      setFilterInvoice('');
      setSearchTerm('');
      setFilterClient('');
      setFilterCase('');
      setCommunicationLogsViewMode('list');

      if (!navigateTo.id) {
        setNavigateTo(null);
      }
    }
  }, [navigateTo, setNavigateTo]);

  useEffect(() => {
    if (navigateTo && navigateTo.tab === 'communicationLogs' && navigateTo.prefillLog) {
      const prefill = navigateTo.prefillLog || {};
      setShowAddCommunicationLogForm(true);
      setNewCommunicationLog((prev) => ({
        ...prev,
        client: prefill.client || '',
        case: prefill.case || '',
        debt_recovery_request: prefill.debt_recovery_request || '',
        invoice: prefill.invoice || '',
        log_type: prefill.log_type || 'Note',
        subject: prefill.subject || '',
        body: prefill.body || '',
        attachment: null,
      }));
      if (prefill.debt_recovery_request_reference) {
        setFilterDebtRecoveryReference(String(prefill.debt_recovery_request_reference));
      }
      setStatusChangeContext(navigateTo.statusChange || null);
      setStatusGuidance(navigateTo.statusGuidance || null);
      setStatusReason('');
      setStatusFollowUpDate('');
      setStatusCommitments('');
      setStatusCreateSettlementTask(false);
      setCommunicationLogsViewMode('list');
      setNavigateTo(null);
    }
  }, [navigateTo, setNavigateTo]);

  useEffect(() => {
    if (navigateTo && navigateTo.tab === 'communicationLogs') {
      const log = communicationLogs.find(l => l.id === navigateTo.id);
      if (log) {
        openDetailsModal(log);
        setNavigateTo(null);
      }
    }
  }, [navigateTo, communicationLogs, openDetailsModal, setNavigateTo]);

  // Auto-open via custom event (open-resource-detail)
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail || detail.tab !== 'communicationLogs') return;
      if (detail.type === 'add_new') {
        setShowAddCommunicationLogForm(true);
        return;
      }
      const log = communicationLogs.find(l => l.id === detail.id);
      if (log) {
        openDetailsModal(log);
        setNavigateTo(null);
      }
    };
    window.addEventListener('open-resource-detail', handler);
    return () => window.removeEventListener('open-resource-detail', handler);
  }, [communicationLogs, openDetailsModal, setNavigateTo]);


  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Communication Logs</h2>

      <div className="flex justify-between items-center mb-6">
        {permissions.canCreateLog && (
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
          onClick={() => setShowAddCommunicationLogForm(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New Log
        </button>
        )}
        <div className="flex space-x-2">
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${communicationLogsViewMode === 'list' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setCommunicationLogsViewMode('list')}>
            List
          </button>
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${communicationLogsViewMode === 'tiles' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setCommunicationLogsViewMode('tiles')}>
            Tiles
          </button>
        </div>
      </div>
      
      {/* Add New Log Side Panel */}
      {showAddCommunicationLogForm && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={resetAddLogForm}></div>
            <section className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
              <div className="w-screen max-w-2xl">
                <div className="h-full flex flex-col bg-white shadow-xl">
                  <div className="px-4 py-6 sm:px-6 bg-gray-50 border-b">
                    <div className="flex items-start justify-between">
                      <h2 className="text-lg font-medium text-gray-900">New Communication Log</h2>
                      <div className="ml-3 h-7 flex items-center">
                        <button onClick={resetAddLogForm} className="bg-gray-50 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <span className="sr-only">Close panel</span>
                          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <form onSubmit={handleCommunicationLogSubmit} className="h-full flex flex-col">
                      <div className="flex-1 p-6 space-y-6">
                        {statusGuidance && (
                          <div className="p-4 rounded-lg border border-blue-200 bg-blue-50 text-sm text-blue-900">
                            <div className="font-semibold">{statusGuidance.title || 'Status Change Guidance'}</div>
                            {statusGuidance.description && (
                              <div className="mt-1 text-blue-800">{statusGuidance.description}</div>
                            )}
                            {statusGuidance.requestReference && (
                              <div className="mt-2 text-xs text-blue-700">DRR: {statusGuidance.requestReference}</div>
                            )}
                            <div className="mt-3 flex flex-wrap gap-2">
                              {statusGuidance.actions?.includes('draft_document') && (
                                <button type="button" className="px-3 py-1.5 rounded bg-white border border-blue-200 text-blue-700 hover:bg-blue-100" onClick={() => openDocumentsForDrr('draft')}>Draft Document</button>
                              )}
                              {statusGuidance.actions?.includes('use_template') && (
                                <button type="button" className="px-3 py-1.5 rounded bg-white border border-blue-200 text-blue-700 hover:bg-blue-100" onClick={() => openDocumentsForDrr('template')}>Use Template</button>
                              )}
                              {statusGuidance.actions?.includes('upload_proof') && (
                                <button type="button" className="px-3 py-1.5 rounded bg-white border border-blue-200 text-blue-700 hover:bg-blue-100" onClick={() => openDocumentsForDrr('upload')}>Upload Proof</button>
                              )}
                              {statusGuidance.actions?.includes('sms_placeholder') && (
                                <button type="button" className="px-3 py-1.5 rounded bg-white border border-blue-200 text-blue-700 hover:bg-blue-100" onClick={handleSmsPlaceholder}>SMS Placeholder</button>
                              )}
                              {statusGuidance.actions?.includes('create_case') && (
                                <button type="button" className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={handleCreateCaseFromDrr}>Create Case</button>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Client:</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="client" value={newCommunicationLog.client} onChange={handleCommunicationLogInputChange} required>
                                    <option value="">Select Client</option>
                                    {(clients || []).map(client => (
                                    <option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Case (Optional):</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="case" value={newCommunicationLog.case} onChange={handleCommunicationLogInputChange} disabled={!newCommunicationLog.client}>
                                    <option value="">Select Case</option>
                                    {availableCasesForNewLog.map(_case => (
                                    <option key={_case.id} value={_case.id}>{_case.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Type:</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" name="log_type" value={newCommunicationLog.log_type} onChange={handleCommunicationLogInputChange} required>
                                    {(communicationLogTypeOptions || []).map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Subject:</label>
                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="subject" value={newCommunicationLog.subject} onChange={handleCommunicationLogInputChange} required />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Body:</label>
                                <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="body" value={newCommunicationLog.body} onChange={handleCommunicationLogInputChange} required></textarea>
                            </div>
                            {statusChangeContext && ['WRITTEN_OFF', 'CLOSED'].includes(statusChangeContext.nextStatus) && (
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Reason (required for closed/written off)</label>
                                <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" value={statusReason} onChange={(e) => setStatusReason(e.target.value)} required></textarea>
                              </div>
                            )}
                            {statusChangeContext && statusChangeContext.nextStatus === 'PARTIALLY_RECOVERED' && (
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Next follow-up date (recommended)</label>
                                <input type="date" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" value={statusFollowUpDate} onChange={(e) => setStatusFollowUpDate(e.target.value)} />
                              </div>
                            )}
                            {statusChangeContext && statusChangeContext.nextStatus === 'PARTIALLY_RECOVERED' && (
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Commitments / notes</label>
                                <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" value={statusCommitments} onChange={(e) => setStatusCommitments(e.target.value)} placeholder="e.g. debtor committed to pay on..." />
                              </div>
                            )}
                            {statusChangeContext && statusChangeContext.nextStatus === 'NEGOTIATION' && (
                              <div className="md:col-span-2 flex items-center gap-2">
                                <input id="createSettlementTask" type="checkbox" checked={statusCreateSettlementTask} onChange={(e) => setStatusCreateSettlementTask(e.target.checked)} />
                                <label htmlFor="createSettlementTask" className="text-sm text-gray-700">Add task: Draft Settlement Document</label>
                              </div>
                            )}
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700">Attachment (optional)</label>
                              <input type="file" name="attachment" className="mt-1 block w-full text-sm" onChange={handleCommunicationLogInputChange} />
                            </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 px-4 py-4 flex justify-end space-x-4 border-t">
                          <button type="button" onClick={resetAddLogForm} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
                          <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">Add Log</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}

      {/* ENHANCEMENT: Search and Filter Controls */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <input 
              type="text" 
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" 
              placeholder="Search logs..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
            />
            <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" value={filterClient} onChange={(e) => setFilterClient(e.target.value)}>
              <option value="">Filter by Client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>
              ))}
            </select>
            <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" value={filterCase} onChange={(e) => setFilterCase(e.target.value)}>
              <option value="">Filter by Case</option>
              {cases.map(_case => (
                <option key={_case.id} value={_case.id}>{_case.title}</option>
              ))}
            </select>
            <input
              type="text"
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
              placeholder="Invoice ID (optional)"
              value={filterInvoice}
              onChange={(e) => setFilterInvoice(e.target.value)}
              inputMode="numeric"
            />
            <input
              type="text"
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
              placeholder="Debt Recovery Ref (optional)"
              value={filterDebtRecoveryReference}
              onChange={(e) => setFilterDebtRecoveryReference(e.target.value)}
            />
            <button className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300" onClick={() => { setSearchTerm(''); setFilterClient(''); setFilterCase(''); setFilterInvoice(''); setFilterDebtRecoveryReference(''); }}>
              Clear
            </button>
          </div>
        </div>


      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-4 border-b">
          <h5 className="text-lg font-bold text-gray-800">All Communication Logs</h5>
        </div>
        <div className="p-6">
          {filteredCommunicationLogs.length > 0 ? (
            communicationLogsViewMode === 'list' ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Case</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Logged By</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCommunicationLogs.map(log => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(log.timestamp).toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-${getBadgeColor(log.log_type)}-100 text-${getBadgeColor(log.log_type)}-800`}>{log.log_type}</span></td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{log.subject}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.client_full_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.case_title || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.logged_by_username}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          {permissions.canEditLog && 
                            <button title="Edit Log" className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100" onClick={() => openEditCommunicationLogModal(log)}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                            </button>
                          }
                          <button title="View Log" className="text-gray-600 hover:text-gray-900 p-1 rounded-full hover:bg-gray-100" onClick={() => openDetailsModal(log)}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                          </button>
                          {permissions.canDeleteLog && (
                            <button
                              title="Delete Log"
                              className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100"
                              onClick={() => handleDelete('communication-logs', log.id, 'Communication Log', fetchCommunicationLogs)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCommunicationLogs.map(log => (
                  <div key={log.id} className="bg-white shadow-md rounded-lg overflow-hidden flex flex-col">
                    <div className="p-6 flex-grow">
                      <h5 className="text-lg font-bold text-gray-800">{log.subject}</h5>
                      <p className="text-sm text-gray-600 mt-2"><strong>Client:</strong> {log.client_full_name}</p>
                      <p className="text-sm text-gray-600"><strong>Case:</strong> {log.case_title || 'N/A'}</p>
                      <p className="text-sm text-gray-500 mt-2">{new Date(log.timestamp).toLocaleString()}</p>
                      <span className={`mt-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-${getBadgeColor(log.log_type)}-100 text-${getBadgeColor(log.log_type)}-800`}>{log.log_type}</span>
                    </div>
                     <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
                        {permissions.canEditLog && 
                            <button title="Edit Log" className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100" onClick={() => openEditCommunicationLogModal(log)}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                            </button>
                        }
                        <button title="View Log" className="text-gray-600 hover:text-gray-900 p-1 rounded-full hover:bg-gray-100" onClick={() => openDetailsModal(log)}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                        </button>
                        {permissions.canDeleteLog && (
                          <button
                            title="Delete Log"
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100"
                            onClick={() => handleDelete('communication-logs', log.id, 'Communication Log', fetchCommunicationLogs)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                          </button>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <p className="text-center text-gray-500">No communication logs found matching your criteria.</p>
          )}
        </div>
      </div>

      {/* Edit Log Side Panel */}
      {showEditCommunicationLogModal && editingCommunicationLog && (
        <div className="fixed inset-0 overflow-hidden z-50">
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeEditCommunicationLogModal}></div>
                <section className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
                    <div className="w-screen max-w-2xl">
                        <div className="h-full flex flex-col bg-white shadow-xl">
                            <div className="px-4 py-6 sm:px-6 bg-gray-50 border-b">
                                <div className="flex items-start justify-between">
                                    <h2 className="text-lg font-medium text-gray-900">Edit Log: {editingCommunicationLog.subject}</h2>
                                    <div className="ml-3 h-7 flex items-center">
                                        <button onClick={closeEditCommunicationLogModal} className="bg-gray-50 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                            <span className="sr-only">Close panel</span>
                                            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                <form onSubmit={handleEditCommunicationLogSubmit} className="h-full flex flex-col">
                                    <div className="flex-1 p-6 space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Client:</label>
                                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md" name="client" value={editingCommunicationLog.client || ''} onChange={handleEditingCommunicationLogChange} required>
                                                <option value="">Select Client</option>
                                                {(clients || []).map(client => (
                                                    <option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>
                                                ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Case (Optional):</label>
                                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md" name="case" value={editingCommunicationLog.case || ''} onChange={handleEditingCommunicationLogChange} disabled={!editingCommunicationLog.client}>
                                                <option value="">Select Case</option>
                                                {availableCasesForEditLog.map(_case => (
                                                    <option key={_case.id} value={_case.id}>{_case.title}</option>
                                                ))}
                                                </select>
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700">Type:</label>
                                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md" name="log_type" value={editingCommunicationLog.log_type || ''} onChange={handleEditingCommunicationLogChange} required>
                                                {(communicationLogTypeOptions || []).map(option => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                                </select>
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700">Subject:</label>
                                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md" name="subject" value={editingCommunicationLog.subject || ''} onChange={handleEditingCommunicationLogChange} required />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700">Body:</label>
                                                <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md" name="body" value={editingCommunicationLog.body || ''} onChange={handleEditingCommunicationLogChange} required></textarea>
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700">Replace attachment (optional)</label>
                                                <input type="file" className="mt-1 block w-full text-sm" onChange={handleEditingAttachmentChange} />
                                                {editingCommunicationLog.attachment && (
                                                  <div className="mt-2 text-xs text-gray-600">
                                                    Current: <a className="text-blue-600 hover:underline" href={editingCommunicationLog.attachment} target="_blank" rel="noreferrer">View attachment</a>
                                                  </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0 px-4 py-4 flex justify-end space-x-4 border-t">
                                        <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md" onClick={closeEditCommunicationLogModal}>Cancel</button>
                                        <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md">Save Changes</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
      </div>
      )}
      
      {/* Details Side Panel */}
      {showDetailsModal && selectedLogForDetails && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
             <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailsModal}></div>
             <section className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
                <div className="w-screen max-w-2xl">
                   <div className="h-full flex flex-col bg-white shadow-xl">
                    <div className="px-4 py-6 sm:px-6 bg-gray-50 border-b">
                       <div className="flex items-start justify-between">
                         <h2 className="text-lg font-medium text-gray-900">Log Details</h2>
                         <div className="ml-3 h-7 flex items-center">
                           <button onClick={closeDetailsModal} className="bg-gray-50 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500">
                             <span className="sr-only">Close panel</span>
                             <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                           </button>
                         </div>
                       </div>
                     </div>
                      <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        <div><strong className="font-semibold text-gray-700">Client:</strong> {selectedLogForDetails.client_full_name}</div>
                        <div><strong className="font-semibold text-gray-700">Case:</strong> {selectedLogForDetails.case_title || 'N/A'}</div>
                        <div><strong className="font-semibold text-gray-700">Type:</strong> {selectedLogForDetails.log_type}</div>
                        <div><strong className="font-semibold text-gray-700">Timestamp:</strong> {new Date(selectedLogForDetails.timestamp).toLocaleString()}</div>
                        <div><strong className="font-semibold text-gray-700">Subject:</strong> {selectedLogForDetails.subject}</div>
                        <div><strong className="font-semibold text-gray-700">Body:</strong> <p className="mt-1 p-2 bg-gray-100 rounded whitespace-pre-wrap">{selectedLogForDetails.body || 'N/A'}</p></div>
                        {selectedLogForDetails.attachment && (
                          <div>
                            <strong className="font-semibold text-gray-700">Attachment:</strong>{' '}
                            <a className="text-blue-600 hover:underline" href={selectedLogForDetails.attachment} target="_blank" rel="noreferrer">Download</a>
                          </div>
                        )}
                        <div><strong className="font-semibold text-gray-700">Logged By:</strong> {selectedLogForDetails.logged_by_username}</div>
                      </div>
                      <div className="flex-shrink-0 px-4 py-4 flex justify-end space-x-4 border-t">
                          <button type="button" onClick={closeDetailsModal} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300">Close</button>
                      </div>
                   </div>
                </div>
             </section>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommunicationLogManagement;
